import {
  Avatar,
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { Link, useLocation } from 'react-router-dom'
import { useAppData } from '../contexts/AppDataContext'
import { useNavItems } from './useNavItems'
import { useAppBase, appPath } from './appBase'
import EventLogo from './EventLogo'

export default function NavRail() {
  const location = useLocation()
  const { attendee } = useAppData()
  const base = useAppBase()
  const navItems = useNavItems()

  const isActive = (to: string) =>
    to === base ? location.pathname === to : location.pathname.startsWith(to)

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 3, py: 2.5 }}>
        <Box component={Link} to={base} sx={{ display: 'inline-flex' }}>
          <EventLogo />
        </Box>
      </Box>

      <Stack spacing={0.5} sx={{ px: 2 }}>
        {navItems.map((item) => {
          const to = appPath(base, item.sub)
          const active = isActive(to)
          return (
            <ListItemButton
              key={item.sub}
              component={Link}
              to={to}
              selected={active}
              sx={{
                borderRadius: 2,
                py: 1.1,
                color: active ? 'primary.main' : 'text.secondary',
                '&.Mui-selected': {
                  bgcolor: 'rgba(0,117,188,0.10)',
                  '&:hover': { bgcolor: 'rgba(0,117,188,0.16)' },
                },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}
              >
                <item.icon />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: active ? 700 : 500,
                  fontSize: '0.95rem',
                }}
              />
            </ListItemButton>
          )
        })}
      </Stack>

      <Box sx={{ flex: 1 }} />

      {attendee && (
        <Box sx={{ p: 2 }}>
          <Box
            component={Link}
            to={appPath(base, 'profile')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              textDecoration: 'none',
              color: 'inherit',
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'border-color .15s',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 40,
                height: 40,
                fontWeight: 700,
                fontSize: '0.9rem',
              }}
            >
              {attendee.initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {attendee.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {attendee.company}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}
